import { create } from "zustand";
import { LOG } from "../utils/logUtil";
import axios from "axios";
import { useAppLoading } from "./useAppLoading";

export interface IDataCarrier {
  _id?: string;
  grade?: string;
  count?: number;
  date?: Date;
  createdAt?: string;
  updatedAt?: string;
}

interface IGradePercentage {
  name: string;
  data: number[];
}

interface IStore {
  /** State */
  hasNextPage: boolean;
  dataCarriers: IDataCarrier[];
  dataCarrierGradePercentageStats: IGradePercentage[];
  /** Callbacks */
  getDataCarrierGradePercentages: (requestedDate: Date) => void;
  getDataCarriers: (page?: number, limit?: number) => void;
  addManyDataCarriers: (data: IDataCarrier[]) => void;
  deleteDataCarrier: (sProductId: string) => void;
}

export const useDataCarriers = create<IStore>()((set) => ({
  /** State */
  dataCarrierGradePercentageStats: [],
  hasNextPage: false,
  dataCarriers: [],
  /** Callbacks */
  getDataCarriers: async (page = 1, limit = 20) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.get(
        `data-carriers?page=${page}&limit=${limit}`
      );
      set((state) => ({
        ...state,
        hasNextPage: response?.data?.length === limit,
        dataCarriers:
          page === 1
            ? response?.data
            : [...state.dataCarriers, ...response?.data],
      }));
    } catch (error) {
      LOG.error("Error on fetching Data Carriers", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  addManyDataCarriers: async (body) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.post("data-carriers/many", body);
      if (response?.data) {
        const newDataCarriers = response.data;
        set((state) => ({
          dataCarriers: [...newDataCarriers, ...state.dataCarriers],
        }));
      }
    } catch (error) {
      LOG.error("Error on adding new Data Carriers", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  deleteDataCarrier: async (sProductId) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      await axios.delete(`data-carriers/${sProductId}`);
      set((state) => ({
        dataCarriers: state.dataCarriers.filter(
          (dcProduct) => dcProduct._id !== sProductId
        ),
      }));
    } catch (error) {
      LOG.error("Error on deleting a Data Carrier", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  getDataCarrierGradePercentages: async (requestedDate) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.get(
        `data-carriers/stats/grade-percentage?requestedDate=${requestedDate}`
      );
      set({ dataCarrierGradePercentageStats: response?.data });
    } catch (error) {
      LOG.error("Error on fetching Data Carrier Grade Percentages", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
}));
